// Extracted from src/features/quotes/DocumentViewPage.tsx (backlog PRINT-26)
// so the exact same markup can be rendered by two different callers:
// DocumentViewPage's authenticated on-screen preview, and the new
// print-only route (PrintDocumentPage) that Cloudflare's Browser Run
// service navigates to when generating a PDF server-side. Same reasoning
// as @/components/snapdraw/aia-sheet.tsx already being its own file for
// the G702/G703 side — reusing the real component is what keeps the
// server-rendered PDF from drifting out of sync with the on-screen
// preview.
//
// This is the generic Quote/Invoice sheet — real totals math via
// @/features/quotes/quoteMath, same as the builder.
import type { ReactNode } from "react";

import { useResolvedLogoUrl } from "@/lib/useResolvedLogoUrl";
import { useResolvedPhotoUrls } from "@/lib/useResolvedPhotoUrls";
import { computeTotals, effectiveRate, lineDisplayTotal, moneyExact, slugify } from "@/features/quotes/quoteMath";
import { displayNumber } from "@/lib/lineage";
import { SaveToDeviceButton } from "@/components/snapdraw/photo-attachments";
import { daysLate, docDateLabel, formatDueDate } from "@/lib/docStatus";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import type { DocPhoto, Invoice, LineItem, StandardQuote } from "@/lib/types";

export function QuoteSheet({
  doc,
  predecessorLabel,
  resolvedPhotoUrls,
  resolvedLogoUrl,
}: {
  doc: StandardQuote | Invoice;
  predecessorLabel?: string | null;
  /**
   * Pre-resolved signed URLs, keyed by DocPhoto.id — set only by the
   * print-only route (PrintDocumentPage), which has no Supabase session
   * to resolve these itself (its Edge Function, print-doc-data, resolves
   * them server-side instead). Omitted here, useResolvedPhotoUrls falls
   * back to its normal authenticated network resolution — see that hook's
   * own comment.
   */
  resolvedPhotoUrls?: Record<string, string>;
  /** Same idea as resolvedPhotoUrls, for the single logo path. */
  resolvedLogoUrl?: string | null;
}) {
  const t = useT();
  const logoUrl = useResolvedLogoUrl(doc.bizLogoPath, resolvedLogoUrl);
  const isInvoice = doc.docType === "invoice";
  const late = daysLate(doc);
  const markupPct = parseFloat(doc.markup) || 0;
  const taxPct = parseFloat(doc.tax) || 0;
  const totals = computeTotals(doc.items, { markup: markupPct, tax: taxPct });
  const lastItem = doc.items[doc.items.length - 1];
  const number = doc.docType === "standard" ? displayNumber(doc) : doc.docId;
  const provenance =
    doc.docType === "standard" && doc.supersedesDocKey && predecessorLabel
      ? t("docs.view.provenance.replaces", { number: predecessorLabel })
      : doc.docType === "invoice" && doc.derivedFromDocKey && predecessorLabel
        ? t("docs.view.provenance.bills", { number: predecessorLabel })
        : null;

  return (
    <article className="sd-sheet sd-sheet-quote sd-card mt-5 p-6 lg:p-10">
      <header className="flex flex-wrap items-start justify-between gap-6 pb-6">
        <div className="min-w-0">
          <div className="sd-label">{t(isInvoice ? "docs.view.kind.invoice" : "docs.view.kind.quote")}</div>
          <h1 className="sd-quote-title mt-2 break-words font-display text-3xl uppercase tracking-wide lg:text-5xl">
            {doc.projectName || doc.docId}
          </h1>
          <div className="sd-numeric mt-3 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            {number.toUpperCase()} · {t("docs.view.rev", { rev: doc.revision })} · {docDateLabel(doc)}
          </div>
          {provenance ? (
            <div className="sd-numeric mt-1 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              {provenance}
            </div>
          ) : null}
          {doc.docType === "standard" && doc.revisionReason ? (
            <div className="mt-2 max-w-md text-sm text-foreground/80">
              <span className="sd-label mr-2">{t("docs.delta.reason")}</span>
              {doc.revisionReason}
            </div>
          ) : null}
        </div>
        <div className="min-w-0 text-right">
          {logoUrl ? <img src={logoUrl} alt="" className="sd-doc-logo" /> : null}
          <div className="sd-label break-words">{doc.bizName}</div>
          <div className="mt-2 text-sm text-muted-foreground">{doc.bizPhone}</div>
          <div className="break-words text-sm text-muted-foreground">{doc.bizEmail}</div>
        </div>
      </header>

      <div className="sd-block sd-quote-sec-billing grid gap-6 border-t border-border py-6 sm:grid-cols-3">
        <div className="min-w-0">
          <div className="sd-label">{t("docs.view.billTo")}</div>
          <div className="mt-2 break-words text-base font-semibold">{doc.clientName}</div>
          <div className="break-words text-sm text-muted-foreground">{doc.clientAddress}</div>
        </div>
        <div>
          <div className="sd-label">{t(isInvoice ? "docs.view.terms" : "docs.view.paymentTerms")}</div>
          {isInvoice ? (
            <>
              <div className="sd-numeric mt-2 text-sm">{t(`docs.due.terms.${doc.termId}`)}</div>
              <div className="sd-numeric mt-1 text-sm text-muted-foreground">
                {doc.status === "draft" || !doc.dueDate
                  ? t("docs.view.noDueDate")
                  : `${t("docs.due.paymentDue")} ${formatDueDate(doc.dueDate)}`}
              </div>
            </>
          ) : (
            <>
              {/* Quotes show only the payment term, never a computed due
                  date (DOCS-14) — a quote's due date was calculated from
                  its own creation date, with no relationship to when the
                  client actually accepts it. Sized up (base/semibold, vs.
                  the invoice branch's plain text-sm) so this single line
                  carries the same visual weight Bill To/Amount Due each
                  get from their own multi-line content. */}
              <div className="sd-numeric mt-2 text-base font-semibold">{t(`docs.due.terms.${doc.termId}`)}</div>
              {doc.validUntil ? (
                <div className="sd-numeric mt-1 text-sm text-muted-foreground">
                  {t("docs.due.validUntil")} {formatDueDate(doc.validUntil)}
                </div>
              ) : null}
            </>
          )}
        </div>
        <div className="sm:text-right">
          <div className="sd-label">{t(isInvoice ? "docs.view.amountDueInvoice" : "docs.view.amountDue")}</div>
          <div className="sd-numeric mt-2 text-3xl font-bold text-arc">{moneyExact(totals.grandTotal)}</div>
          {late > 0 ? (
            <div className="sd-numeric mt-1 text-[11px] uppercase tracking-[0.16em] text-warning">
              {t("docs.late", { days: late })}
            </div>
          ) : null}
        </div>
      </div>

      {/* PRINT-9: a real <table>/<thead>, not a styled grid — a browser's
          native "repeat the header row on every physical page" behavior
          (the same mechanism AiaSheet's G703 table already relies on,
          aia-sheet.tsx) only exists for actual table markup. A CSS grid has
          no equivalent, so a long quote's page-2+ continuation used to have
          no header row at all. Safe to do now in a way it wasn't when
          AiaSheet's own header-repeat was fought over 5 separate times
          (see tailwind-preview.css's PRINT-6 history): every PDF now goes
          through one single, server-controlled Chromium (Cloudflare Browser
          Run, backlog PRINT-26), not whichever browser the customer's own
          machine happens to run print through — the Chrome-vs-Safari
          divergence that made this fragile before no longer applies. */}
      {/* pt-6 only (not py-6): the bottom half used to double up with the
          last item's own gap below -- that item lives in a SEPARATE table
          (see the comment below), so the boundary between this table's
          last row and that one already gets its own spacing from each
          row's py-3 plus that table's border-t, matching every other
          item-to-item gap (row py-3 + divide-y + row py-3). A trailing
          py-6 here on top of that stacked to roughly double the gap at
          just that one boundary -- confirmed live, real user-flagged
          inconsistency (Q-2026-1102, "Paint & Supplies" -> "Dumpster
          Rental (Weekly)"). */}
      <div className="sd-quote-sec-items border-t border-border pt-6">
        <div className="sd-label font-bold">{t("docs.quote.lineItems")}</div>
        <table className="sd-quote-line-table mt-4 block w-full border-collapse lg:table lg:table-fixed">
          <colgroup>
            <col className="w-auto" />
            <col className="w-[90px]" />
            <col className="w-[90px]" />
            <col className="w-[110px]" />
            <col className="w-[120px]" />
          </colgroup>
          <thead className="sd-quote-line-header hidden border-b border-border lg:table-header-group">
            <tr className="lg:table-row">
              <Th>{t("docs.quote.col.description")}</Th>
              <Th right>{t("docs.view.col.unit")}</Th>
              <Th right>{t("docs.quote.col.qtyHrs")}</Th>
              <Th right>{t("docs.quote.col.rate")}</Th>
              <Th right>{t("docs.quote.col.total")}</Th>
            </tr>
          </thead>
          <tbody className="block divide-y divide-border lg:table-row-group">
            {doc.items.slice(0, -1).map((line) => (
              <QuoteLineItemRow key={line.id} line={line} docMarkupPct={markupPct} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Last line item + totals ladder grouped so a natural page break can't
          separate the totals from the row they total (Lovable feedback,
          2026-08-27) — .sd-block's break-inside: avoid only covers a single
          element, so the last row moves out of the item list above and into
          this wrapper alongside the totals themselves. Its own small table
          (sharing the same column widths via the colgroup pattern above, no
          header of its own — never more than one row, always glued to the
          totals right below it, so there's nothing for a header to do here). */}
      <div className="sd-block">
        {lastItem ? (
          <table
            className={cn(
              "sd-quote-sec-totals-lead block w-full border-collapse lg:table lg:table-fixed",
              doc.items.length > 1 && "border-t border-border",
            )}
          >
            <colgroup>
              <col className="w-auto" />
              <col className="w-[90px]" />
              <col className="w-[90px]" />
              <col className="w-[110px]" />
              <col className="w-[120px]" />
            </colgroup>
            <tbody className="block lg:table-row-group">
              <QuoteLineItemRow line={lastItem} docMarkupPct={markupPct} />
            </tbody>
          </table>
        ) : null}
        <div className="sd-quote-sec-totals ml-auto max-w-sm border-t border-border pt-4">
          {/* Backlog PRINT-8: a printed/sent document is client-facing, so an
              intermediate line that's genuinely $0 (no materials on a
              labor-only quote, 0% tax) is clutter, not useful information —
              suppress it here. Grand Total always shows regardless (even a
              genuinely $0 quote should say so). The live builder
              (QuoteBuilderPage) is unchanged — while actively editing,
              seeing the full ladder is useful, not noise. Markup never gets
              its own row here at all (backlog AUTH-8 follow-up, 2026-09-05,
              confirmed with Gabriel) — materialsBase already folds it in, so
              Labor + Materials + Tax still reconciles to Grand Total without
              ever disclosing a markup figure to the customer. */}
          {totals.laborSubtotal > 0 && (
            <TotalLine label={t("docs.quote.total.laborSubtotal")} value={moneyExact(totals.laborSubtotal)} />
          )}
          {totals.materialsBase + totals.materialsMarkup > 0 && (
            <TotalLine label={t("docs.quote.total.equipment")} value={moneyExact(totals.materialsBase + totals.materialsMarkup)} />
          )}
          {totals.taxAmount > 0 && (
            <TotalLine label={t("docs.quote.total.salesTaxPct", { pct: taxPct })} value={moneyExact(totals.taxAmount)} />
          )}
          <TotalLine
            label={t(isInvoice ? "docs.view.total.dueInvoice" : "docs.view.total.due")}
            value={moneyExact(totals.grandTotal)}
            emphasis
          />
        </div>
      </div>

      {/* Photos are quote-only (backlog DOCS-20), placed after the totals
          and before the words — same "money, then evidence, then argument"
          reasoning as their placement in the builder (QuoteBuilderPage). */}
      {doc.docType === "standard" && doc.photos && doc.photos.length > 0 ? (
        <PhotoSheetSection
          photos={doc.photos}
          resolvedPhotoUrls={resolvedPhotoUrls}
          filenamePrefix={slugify(doc.docId) || "photo"}
        />
      ) : null}

      {doc.scopeOfWork || doc.exclusions ? (
        <div className="sd-block sd-quote-sec-scope grid gap-6 border-t border-border py-6 sm:grid-cols-2">
          {doc.scopeOfWork ? (
            <div>
              <div className="sd-label">{t("docs.quote.scope.work")}</div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/90">{doc.scopeOfWork}</p>
            </div>
          ) : null}
          {doc.exclusions ? (
            <div>
              <div className="sd-label">{t("docs.quote.scope.exclusions")}</div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/90">{doc.exclusions}</p>
            </div>
          ) : null}
        </div>
      ) : null}

      {!isInvoice ? (
        <div className="sd-block sd-quote-sec-accept border-t border-border py-6">
          <div className="sd-label">{t("docs.quote.accept.title")}</div>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">{t("docs.quote.accept.body")}</p>
          <div className="sd-quote-accept-signature-row mt-6 grid gap-6 sm:grid-cols-[minmax(0,1fr)_140px]">
            <div>
              <div className="h-12 border-b border-border-strong" />
              <span className="sd-label mt-2 block">{t("docs.quote.accept.signature")}</span>
            </div>
            <div>
              <div className="h-12 border-b border-border-strong" />
              <span className="sd-label mt-2 block">{t("docs.quote.accept.date")}</span>
            </div>
          </div>
          <div className="sd-quote-accept-printname-row mt-6 grid gap-6 sm:grid-cols-2 sm:max-w-lg">
            <div>
              <div className="h-12 border-b border-border-strong" />
              <span className="sd-label mt-2 block">{t("docs.quote.accept.printName")}</span>
            </div>
            <div>
              <div className="h-12 border-b border-border-strong" />
              <span className="sd-label mt-2 block">{t("docs.quote.accept.printTitle")}</span>
            </div>
          </div>
        </div>
      ) : null}

      <footer className="mt-8 border-t border-border pt-5 font-mono text-[10px] uppercase tracking-[0.16em] text-arc">
        {t("docs.view.footer")}
      </footer>
    </article>
  );
}

/**
 * Site photos (backlog DOCS-20) — same label/rule/content shape as every
 * other section on this sheet. Fixed-height tile + object-contain so
 * mixed portrait/landscape phone photos both fit without cropping (the
 * letterbox is intentional); .sd-photo-tile (src/styles/tailwind-preview.css)
 * gives each tile break-inside: avoid so an image never lands on one page
 * with its caption on the next, while the section itself is free to flow
 * across a page break when there are more photos than fit on one page.
 * The bucket is private, so each path is resolved to a fresh signed URL at
 * render time rather than stored — see @/lib/photoUpload.
 */
function PhotoSheetSection({
  photos,
  resolvedPhotoUrls,
  filenamePrefix,
}: {
  photos: DocPhoto[];
  resolvedPhotoUrls?: Record<string, string>;
  filenamePrefix: string;
}) {
  const t = useT();
  const resolvedUrls = useResolvedPhotoUrls(photos, resolvedPhotoUrls);

  return (
    // PRINT-25: NOT .sd-block -- that class means break-inside: avoid
    // (tailwind-preview.css), which contradicts this section's whole
    // point of being allowed to flow across a page break when there are
    // more photos than fit on one page (see the comment above). Putting
    // it here was the real bug: it marked the entire multi-photo section
    // (backed by a CSS grid, itself fragmentation-unsafe in Chrome) as
    // one non-splittable unit, which is what let Chrome silently drop
    // everything past wherever that unit stopped fitting.
    <div className="sd-quote-sec-photos border-t border-border py-6">
      <div className="flex flex-wrap items-baseline gap-3">
        <div className="sd-label">{t("photos.sheet.title")}</div>
        <div className="sd-numeric ml-auto text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          {photos.length === 1 ? t("photos.sheet.noteSingular") : t("photos.sheet.notePlural", { count: photos.length })}
        </div>
      </div>
      <ul className="sd-photos mt-4 grid grid-cols-2 gap-5">
        {photos.map((photo, index) => (
          <li key={photo.id} className="sd-photo-tile">
            <div className="group relative flex h-56 items-center justify-center overflow-hidden rounded-sm border border-border bg-black/20">
              {resolvedUrls[photo.id] ? (
                <img
                  src={resolvedUrls[photo.id]}
                  alt={photo.caption || t("photos.position", { n: index + 1 })}
                  // PRINT-25: this sheet exists to be printed/saved as a
                  // PDF in full, regardless of scroll position -- "lazy"
                  // only loads what's near the viewport, so a below-the-
                  // fold photo could still be blank in the exported file
                  // even after the pagination fix.
                  loading="eager"
                  className="max-h-full max-w-full object-contain"
                />
              ) : null}
              {resolvedUrls[photo.id] ? (
                <SaveToDeviceButton url={resolvedUrls[photo.id]} filename={`${filenamePrefix}-photo-${index + 1}.jpg`} />
              ) : null}
            </div>
            {photo.caption ? (
              <div className="mt-2 flex gap-2 border-t border-border pt-2">
                <span className="sd-numeric text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="text-[11px] leading-relaxed text-muted-foreground">{photo.caption}</span>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The line total shown here already includes this line's own effective
 * markup — never a separate, disclosed figure (backlog AUTH-8 follow-up,
 * 2026-09-05, confirmed with Gabriel): a customer-facing view never shows
 * markup as its own line or percentage, on any line or in the summary. Rate
 * is shown as `effectiveRate()`, not the raw `line.rate`, so qty × rate still
 * equals the printed total — a customer doing that multiplication by hand
 * must land on the same number, not a lower one with no markup line to
 * explain the gap. Rate uses `moneyExact()`, not `money()`: `money()` rounds
 * to whole dollars above $100, which would silently break that same
 * reconciliation for any rate ≥ $100 that isn't a whole-dollar amount.
 */
function QuoteLineItemRow({ line, docMarkupPct }: { line: LineItem; docMarkupPct: number }) {
  const t = useT();
  return (
    <tr className="sd-quote-line-item grid gap-1 py-3 lg:table-row">
      {/* py-3 above only matters below lg: -- padding has no effect once
          this becomes a real table-row (lg:table-row), so no lg:py-0
          override is needed. Fixes the divider/next-item-title having
          zero breathing room against each row's own p-0 cells (real
          user-flagged issue, seen live on Q-2026-1102's line items). */}
      <td className="min-w-0 p-0 lg:py-3">
        <div className="sd-quote-line-desc truncate text-sm font-semibold">{line.desc}</div>
      </td>
      <td className="sd-quote-line-cell sd-quote-line-unit sd-numeric p-0 text-[11px] uppercase tracking-[0.16em] text-muted-foreground lg:py-3 lg:text-right">
        {line.type}
      </td>
      {/* Below lg:, the real <thead> is hidden entirely (no room for a
          header row against a stacked layout), so Qty/Rate/Total would
          otherwise be three bare numbers with nothing but position to
          tell them apart -- confirmed a real, pre-existing gap (not new,
          just newly visible now that PRINT-24 put this on legible white
          instead of dark-on-dark). Each cell gets its own inline mono
          label, hidden again at lg: since the real header already covers
          it there. */}
      <td className="sd-quote-line-cell sd-quote-line-qty sd-numeric flex items-baseline justify-between gap-2 p-0 text-sm lg:table-cell lg:py-3 lg:text-right">
        <span className="sd-label print:hidden lg:hidden">{t("docs.quote.col.qtyHrs")}</span>
        <span>{line.qty}</span>
      </td>
      <td className="sd-quote-line-cell sd-numeric flex items-baseline justify-between gap-2 p-0 text-sm text-muted-foreground lg:table-cell lg:py-3 lg:text-right">
        <span className="sd-label print:hidden lg:hidden">{t("docs.quote.col.rate")}</span>
        <span>{moneyExact(effectiveRate(line, docMarkupPct))}</span>
      </td>
      <td className="sd-quote-line-cell sd-numeric flex items-baseline justify-between gap-2 p-0 text-sm font-semibold lg:table-cell lg:py-3 lg:text-right">
        <span className="sd-label print:hidden lg:hidden">{t("docs.quote.col.total")}</span>
        <span>{moneyExact(lineDisplayTotal(line, docMarkupPct))}</span>
      </td>
    </tr>
  );
}

/** Matches the column header cells in the line-items table above the totals. */
function Th({ children, right }: { children?: ReactNode; right?: boolean }) {
  return <th className={cn("sd-label px-0 pb-3 text-left font-normal", right && "text-right")}>{children}</th>;
}

function TotalLine({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className={cn("flex items-center justify-between py-2", emphasis && "mt-2 border-t border-border-strong pt-3")}>
      <span className="sd-label">{label}</span>
      <span className={cn("sd-numeric text-sm font-semibold", emphasis && "text-xl font-bold text-arc")}>{value}</span>
    </div>
  );
}
